import * as zod from 'zod';

type EndEffectorTCP = `ee-${string}`;

export function isEndEffectorTCP(value: unknown): value is EndEffectorTCP {
  return typeof value === 'string' && value.startsWith('ee-');
}

export const TCPOffsetOption = zod
  .preprocess(
    (value) => {
      // legacy endeffector tcp options were raw numbers
      return typeof value === 'number' ? `ee-${value}` : value;
    },
    // union of valid options
    zod.union([
      zod.literal('wrist'),
      zod.literal('wristCamera'),
      zod.custom<EndEffectorTCP>(isEndEffectorTCP),
    ]),
  )
  // if we add more options later,
  // the catch will let us downgrade with fewer problems
  .catch('wrist');

export type TCPOffsetOption = zod.infer<typeof TCPOffsetOption>;

export const DEFAULT_TCP_OFFSET_OPTION: TCPOffsetOption = 'wrist';

export const TCPOptionOrAuto = zod.literal('auto').or(TCPOffsetOption);

/**
 * A fully-determined TCP frame: the option name plus, when known, the exact
 * equipment instance that defines it. `gripperID` matters because an `ee-*`
 * name alone under-determines the frame — several equipment rows can share a
 * kind (so the name is ambiguous), and device kinematics fall back to their
 * own offset for foreign `ee-*` names — while resolution by id works even for
 * disabled equipment. `gripperID: undefined` means "resolve the name against
 * enabled equipment", exactly like command consumption without a gripperID.
 */
export type TCPFrame = {
  tcpOption: TCPOffsetOption;
  gripperID?: string;
};

export function tcpFramesEqual(a: TCPFrame, b: TCPFrame): boolean {
  return a.tcpOption === b.tcpOption && a.gripperID === b.gripperID;
}

/**
 * Parse the desired tooltip index from a tcpOption.
 * Returns -1 to indicate tool0 (wrist/wristCamera) or non-EE options.
 * Supports numeric suffixes and named multi-tip devices (primary/secondary).
 */
export function getTCPIndexFromTCPOption(tcpOption: TCPOffsetOption): number {
  if (tcpOption === 'wrist' || tcpOption === 'wristCamera') return -1;
  if (!isEndEffectorTCP(tcpOption)) return -1;

  const parts = tcpOption.split('-');
  const rest = parts.slice(1); // remove 'ee'

  // numeric suffix
  const last = rest[rest.length - 1];
  const numericIndex = Number.parseInt(last, 10);
  if (!Number.isNaN(numericIndex)) return numericIndex;

  // named suffixes for multi-tip devices
  if (last === 'primary') return 0;
  if (last === 'secondary') return 1;

  // default for single-tip named grippers
  return 0;
}
